/**
 * PUTTING A LINK ON TEXT, without asking the browser to still have the selection.
 *
 * The bug this replaces has been on the list for weeks and never reproduced: "link on highlighted
 * text". It does not reproduce in Chromium — I drove it on 26 August and the anchor lands exactly
 * where it should. What it depended on is the part that differs between engines:
 *
 *   1. the toolbar button opens a URL field, which takes focus;
 *   2. focusing a form control clears the document selection;
 *   3. pressing Add refocuses the editor and re-adds a saved `Range`;
 *   4. `document.execCommand('createLink')` then acts on whatever the selection NOW is.
 *
 * Step 3 is a request, not a guarantee. Safari — which is what every manager in this league runs,
 * on a phone, from a home-screen shortcut — does not reliably restore a selection into a
 * contenteditable that has just regained focus, and on iOS the keyboard opening is a second
 * chance for it to be dropped. When it is dropped the selection reads as COLLAPSED, and the old
 * code's collapsed branch does something perfectly sensible and completely wrong: it writes the
 * URL into the document as its own link and leaves the highlighted words unlinked. Which is
 * precisely the report.
 *
 * So none of it goes through the live selection any more. The `Range` captured when the field
 * opened is the input, and the anchor is built with ordinary DOM calls that cannot be second-
 * guessed by an engine. `execCommand` is gone from this path — the selection is restored
 * afterwards as a courtesy to the caret, and nothing depends on whether that works.
 */

const isAnchor = (n: Node | null): n is HTMLAnchorElement => Boolean(n && n.nodeName === 'A');

/** The anchor a node sits inside, stopping at the editor's own boundary. */
export function anchorAround(node: Node | null, root: HTMLElement): HTMLAnchorElement | null {
  let n: Node | null = node;
  while (n && n !== root) {
    if (isAnchor(n)) return n;
    n = n.parentNode;
  }
  return null;
}

/** Replace an anchor with its own children, leaving the text exactly where it was. */
export function unwrap(a: HTMLAnchorElement): void {
  const parent = a.parentNode;
  if (!parent) return;
  while (a.firstChild) parent.insertBefore(a.firstChild, a);
  parent.removeChild(a);
}

export interface LinkResult {
  /** The anchor now carrying the link, or null if nothing could be done. */
  anchor: HTMLAnchorElement | null;
  /** What actually happened, for the tests and for nothing else. */
  did: 'retargeted' | 'wrapped' | 'inserted' | 'nothing';
}

/**
 * Apply `url` to `range`, inside `root`.
 *
 * Three cases, in the order they are asked about:
 *
 *   ALREADY A LINK — the caret or selection is inside an anchor. Change its href. Wrapping a
 *   second anchor round the first would nest them, and nested anchors are invalid HTML that
 *   browsers silently flatten in whichever direction they feel like.
 *
 *   A SELECTION — extract it, put it inside a new anchor, drop the anchor back in its place.
 *   `extractContents` rather than `surroundContents` because the latter throws on any range that
 *   partially selects an element, which is most of the interesting ones: half a bolded phrase, a
 *   selection that starts mid-word and ends inside an `<em>`.
 *
 *   A CARET — write the URL as its own link, which is what everybody expects from pressing the
 *   link button with nothing selected, plus a space so the next thing typed is not part of it.
 */
export function linkRange(range: Range, url: string, root: HTMLElement): LinkResult {
  const doc = root.ownerDocument;
  if (!doc) return { anchor: null, did: 'nothing' };

  const existing = anchorAround(range.startContainer, root)
    ?? anchorAround(range.endContainer, root);
  if (existing) {
    existing.setAttribute('href', url);
    return { anchor: existing, did: 'retargeted' };
  }

  if (range.collapsed) {
    const a = doc.createElement('a');
    a.setAttribute('href', url);
    a.textContent = url;
    range.insertNode(a);
    /* A space after it, so the next character typed is not swallowed into the link. */
    const after = doc.createTextNode(' ');
    a.parentNode?.insertBefore(after, a.nextSibling);
    return { anchor: a, did: 'inserted' };
  }

  const frag = range.extractContents();
  /*
   * ANY ANCHOR CAUGHT INSIDE THE SELECTION IS UNWRAPPED FIRST. Linking a stretch of text that
   * already contains a link is a perfectly ordinary thing to do by accident, and the result has
   * to be one anchor rather than one containing another.
   */
  for (const inner of Array.from(frag.querySelectorAll('a'))) unwrap(inner);

  const a = doc.createElement('a');
  a.setAttribute('href', url);
  a.appendChild(frag);
  range.insertNode(a);
  return { anchor: a, did: 'wrapped' };
}

/** Put the caret just after `node`, which is where somebody wants it after linking. */
export function caretAfter(node: Node): void {
  const doc = node.ownerDocument;
  const sel = doc?.defaultView?.getSelection();
  if (!doc || !sel) return;
  const r = doc.createRange();
  r.setStartAfter(node);
  r.collapse(true);
  sel.removeAllRanges();
  sel.addRange(r);
}
